import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Keeps the scroll container's relative position (as a fraction of its
 * scrollable range, not "jump to bottom") stable across a layout change that
 * resizes it without remounting — e.g. Bizy's fullscreen toggle, which
 * reflows messages to a different height, leaving the same raw `scrollTop`
 * pointing somewhere else.
 *
 * The ratio is tracked continuously via a passive scroll listener rather
 * than captured on `layoutKey` change, since by the time an effect could
 * react to that change the reflow — and the old scrollHeight — are already
 * gone. The restore runs in `useLayoutEffect`, before paint.
 */
export function useBizyPreserveScrollOnLayoutChange(containerRef: RefObject<HTMLElement>, layoutKey: unknown) {
	const scrollRatioRef = useRef<number | null>(null);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const updateRatio = () => {
			const maxScroll = container.scrollHeight - container.clientHeight;
			scrollRatioRef.current = maxScroll > 0 ? container.scrollTop / maxScroll : 1;
		};

		updateRatio();
		container.addEventListener('scroll', updateRatio, { passive: true });
		return () => container.removeEventListener('scroll', updateRatio);
	}, [containerRef]);

	const prevLayoutKeyRef = useRef(layoutKey);
	useLayoutEffect(() => {
		if (prevLayoutKeyRef.current === layoutKey) return;
		prevLayoutKeyRef.current = layoutKey;

		const container = containerRef.current;
		if (!container || scrollRatioRef.current === null) return;
		const maxScroll = container.scrollHeight - container.clientHeight;
		container.scrollTop = maxScroll > 0 ? scrollRatioRef.current * maxScroll : 0;
	}, [layoutKey, containerRef]);
}
